import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Minus, Plus, RotateCcw } from 'lucide-react';
import { PRODUCTS } from '@/data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InventoryPanel({ token, lang }) {
  const [inventory, setInventory] = useState({});
  const [drafts, setDrafts] = useState({}); // slug -> string value while typing
  const headers = { 'X-Admin-Token': token };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory`);
      setInventory(data || {});
    } catch (_e) {
      toast.error(lang === 'es' ? 'No se pudo cargar inventario' : 'Could not load inventory');
    }
  };

  useEffect(() => { load(); }, []);

  const adjust = async (slug, delta) => {
    try {
      const { data } = await axios.post(`${API}/admin/inventory/${slug}/adjust`, { delta }, { headers });
      setInventory((m) => ({ ...m, [slug]: { stock: data.stock, initial_stock: data.initial_stock } }));
    } catch (_e) {
      toast.error('Error');
    }
  };

  const setExact = async (slug) => {
    const raw = drafts[slug];
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error(lang === 'es' ? 'Valor inválido' : 'Invalid value');
      return;
    }
    try {
      const { data } = await axios.patch(`${API}/admin/inventory/${slug}`, { stock: parsed }, { headers });
      setInventory((m) => ({ ...m, [slug]: { stock: data.stock, initial_stock: data.initial_stock } }));
      setDrafts((d) => ({ ...d, [slug]: '' }));
      toast.success(lang === 'es' ? `Stock actualizado a ${data.stock}` : `Stock set to ${data.stock}`);
    } catch (_e) {
      toast.error('Error');
    }
  };

  const reset = async (slug) => {
    if (!window.confirm(lang === 'es' ? '¿Restablecer a 100 unidades?' : 'Reset to 100 units?')) return;
    try {
      const { data } = await axios.patch(`${API}/admin/inventory/${slug}`, { stock: 100 }, { headers });
      setInventory((m) => ({ ...m, [slug]: { stock: data.stock, initial_stock: data.initial_stock } }));
      toast.success(lang === 'es' ? 'Stock restablecido a 100' : 'Reset to 100');
    } catch (_e) {
      toast.error('Error');
    }
  };

  return (
    <section className="mt-8 rounded-3xl bg-white border border-[#A99BE0]/20 shadow-sm overflow-hidden" data-testid="inventory-panel">
      <header className="px-5 py-4 border-b border-[#A99BE0]/15 bg-gradient-to-r from-[#FFE6EB] to-[#FAEFC2]/40">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-[#FF2D9C]" />
          <div>
            <h2 className="font-display text-xl md:text-2xl font-black tracking-tighter text-[#1F1A24]">
              {lang === 'es' ? 'Inventario' : 'Inventory'}
            </h2>
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#574A66]">
              {lang === 'es' ? '100 unidades por referencia · descuento manual' : '100 units per reference · manual decrement'}
            </p>
          </div>
        </div>
      </header>

      <ul className="divide-y divide-[#A99BE0]/10">
        {PRODUCTS.map((p) => {
          const entry = inventory[p.slug] || { stock: 0, initial_stock: 100 };
          const stock = entry.stock;
          const initial = entry.initial_stock || 100;
          const sold = Math.max(0, initial - stock);
          const pct = initial > 0 ? Math.min(100, (stock / initial) * 100) : 0;
          const isOut = stock <= 0;
          const isLow = stock > 0 && stock <= 10;
          const draft = drafts[p.slug] ?? '';
          return (
            <li key={p.slug} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`inv-row-${p.slug}`}>
              <div className="flex items-center gap-3 min-w-0 md:w-72">
                <div className="w-12 h-12 rounded-2xl flex-shrink-0 overflow-hidden" style={{ backgroundColor: p.color }}>
                  <img src={p.image} alt={p.name[lang]} className="w-full h-full object-contain p-1" loading="lazy" />
                </div>
                <div className="min-w-0">
                  <p className="font-display font-bold text-base text-[#1F1A24] leading-tight truncate">{p.name[lang]}</p>
                  <p className="text-[10px] tracking-widest uppercase font-bold text-[#574A66]">{p.slug}</p>
                </div>
              </div>

              {/* Stock visualization */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between mb-1.5">
                  <p className={`font-display text-2xl font-black tracking-tighter tabular-nums ${isOut ? 'text-[#1F1A24]' : isLow ? 'text-[#FF2D9C]' : 'text-[#1F1A24]'}`}>
                    {stock} <span className="text-xs font-normal text-[#574A66]">/ {initial}</span>
                  </p>
                  <p className="text-[10px] tracking-widest uppercase font-bold text-[#574A66]">
                    {sold > 0 && (lang === 'es' ? `Vendidas: ${sold}` : `Sold: ${sold}`)}
                    {isOut && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#1F1A24] text-white">{lang === 'es' ? 'AGOTADO' : 'SOLD OUT'}</span>}
                    {isLow && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FF2D9C] text-white">{lang === 'es' ? `ÚLTIMAS ${stock}` : `${stock} LEFT`}</span>}
                  </p>
                </div>
                <div className="h-2 rounded-full bg-[#F0EBFC] overflow-hidden">
                  <div
                    className={`h-full transition-all ${isOut ? 'bg-[#1F1A24]' : isLow ? 'bg-[#FF2D9C]' : 'bg-gradient-to-r from-[#FF7597] to-[#A99BE0]'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
                <button
                  type="button"
                  data-testid={`inv-minus-${p.slug}`}
                  onClick={() => adjust(p.slug, -1)}
                  disabled={isOut}
                  className="h-9 w-9 rounded-full bg-[#1F1A24] text-white inline-flex items-center justify-center hover:bg-[#FF2D9C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Restar 1"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid={`inv-plus-${p.slug}`}
                  onClick={() => adjust(p.slug, +1)}
                  className="h-9 w-9 rounded-full bg-[#FF2D9C] text-white inline-flex items-center justify-center hover:bg-[#FF1F8A] transition-colors"
                  aria-label="Sumar 1"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <input
                  data-testid={`inv-input-${p.slug}`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder={lang === 'es' ? 'Fijar' : 'Set'}
                  value={draft}
                  onChange={(e) => setDrafts((d) => ({ ...d, [p.slug]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') setExact(p.slug); }}
                  className="w-20 px-2 py-1.5 rounded-xl text-sm border border-[#A99BE0]/30 focus:outline-none focus:border-[#FF2D9C]"
                />
                <button
                  type="button"
                  data-testid={`inv-set-${p.slug}`}
                  onClick={() => setExact(p.slug)}
                  disabled={!draft}
                  className="px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase bg-[#A99BE0] text-white hover:bg-[#7C5DA5] disabled:opacity-40 transition-colors"
                >
                  OK
                </button>
                <button
                  type="button"
                  data-testid={`inv-reset-${p.slug}`}
                  onClick={() => reset(p.slug)}
                  className="h-9 w-9 rounded-full bg-white border border-[#A99BE0]/30 text-[#574A66] inline-flex items-center justify-center hover:bg-[#FAEFC2] transition-colors"
                  aria-label="Reset 100"
                  title={lang === 'es' ? 'Restablecer a 100' : 'Reset to 100'}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
